import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

/**
 * Language switcher.
 *
 *  - Default (`variant="dropdown"`): pill button with a flag + code, clicking
 *    opens a small popover with flagged options.
 *  - `variant="inline"`: chip-style FR / EN buttons with flags, for headers
 *    or footers that need a compact footprint.
 */
const FLAGS = { fr: '🇫🇷', en: '🇬🇧' };

export default function LanguageSwitcher({ variant = 'dropdown', className = '' }) {
    const { i18n } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const change = (code) => {
        if (code !== current) {
            i18n.changeLanguage(code);
            try { localStorage.setItem('lang', code); } catch { /* ignore quota errors */ }
        }
        setOpen(false);
    };

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (variant === 'inline') {
        return (
            <div className={`inline-flex items-center gap-1 ${className}`} data-testid="lang-switcher-inline">
                {SUPPORTED_LANGUAGES.map((l) => (
                    <button
                        key={l.code}
                        type="button"
                        onClick={() => change(l.code)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                            current === l.code
                                ? 'bg-[#002FA7] text-white'
                                : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                        }`}
                        data-testid={`lang-switch-${l.code}`}
                    >
                        <span aria-hidden="true">{FLAGS[l.code] || '🏳️'}</span>
                        <span className="uppercase tracking-wide">{l.code}</span>
                    </button>
                ))}
            </div>
        );
    }

    const label = SUPPORTED_LANGUAGES.find((l) => l.code === current)?.label || current.toUpperCase();

    return (
        <div ref={ref} className={`relative ${className}`} data-testid="lang-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-md px-2.5 py-1.5 text-sm text-[#0A0A0A] hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid="lang-switcher-trigger"
            >
                <span aria-hidden="true" className="text-base leading-none">{FLAGS[current] || '🏳️'}</span>
                <span className="uppercase font-medium text-xs">{current}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#6B7280] transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <ul
                    className="absolute right-0 top-full mt-1 min-w-[140px] bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 z-50"
                    role="listbox"
                    data-testid="lang-switcher-menu"
                >
                    {SUPPORTED_LANGUAGES.map((l) => (
                        <li key={l.code}>
                            <button
                                type="button"
                                onClick={() => change(l.code)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#F9FAFB] transition-colors ${
                                    current === l.code ? 'text-[#002FA7] font-semibold' : 'text-[#0A0A0A]'
                                }`}
                                aria-label={label}
                                data-testid={`lang-switch-${l.code}`}
                            >
                                <span aria-hidden="true" className="text-base leading-none">{FLAGS[l.code] || '🏳️'}</span>
                                <span className="uppercase text-xs tracking-wide">{l.code}</span>
                                <span className="text-[#6B7280] text-xs">{l.label}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
